import { useMemo } from "react";

import FlexWrapper, { FlexDirection } from "@gx/dls/containers/FlexWrapper";
import GridWrapper, { GridAlignContent, GridAlignItems } from "@gx/dls/containers/GridWrapper";
import { SourceSyncOperationStatus } from "@gx/ts-contracts/services/database/models/public_pb";
import { SourceSyncOperationResource } from "@gx/ts-contracts/services/source/resources/source_sync_operation_pb";

import EmptyLayout from "@/layouts/EmptyLayout";

import { formatFileSize } from "@/pages/sources/components/dropzone/utils";
import EditSourceContentToolbar from "@/pages/sources/components/edit/tabs/content/EditSourceContentToolbar";
import { isSourceSyncOperationFileUpload } from "@/pages/sources/components/edit/tabs/content/types";
import SourceContentFileBadge from "@/pages/sources/components/SourceContentFileBadge";

interface EditSourceContentFileUploadProps {
  operations: SourceSyncOperationResource[];
  search: string;
  onSearchChange: (value: string) => void;
}

const EditSourceContentFileUpload = ({
  operations,
  search,
  onSearchChange,
}: EditSourceContentFileUploadProps) => {
  const fileUploadOperations = useMemo(() => {
    const ops = operations.filter(isSourceSyncOperationFileUpload);

    const query = search.trim().toLowerCase();
    if (!query) return ops;

    return ops.filter((op) => op.metadata.metadata.value.sourcePath?.toLowerCase().includes(query));
  }, [operations, search]);

  return (
    <FlexWrapper direction={FlexDirection.COLUMN} fillWidth fillHeight minHeight={0}>
      <EditSourceContentToolbar
        search={search}
        count={fileUploadOperations.length}
        onSearchChange={onSearchChange}
      />
      <FlexWrapper padding="12px" fillWidth fillHeight minHeight={0} overflow="auto">
        {fileUploadOperations.length > 0 ? (
          <GridWrapper
            columns="repeat(auto-fill, minmax(240px, 1fr))"
            gap={8}
            alignItems={GridAlignItems.START}
            alignContent={GridAlignContent.START}
            fillWidth
          >
            {fileUploadOperations.map((op) => (
              <SourceContentFileBadge
                key={op.id}
                header={op.metadata.metadata.value.sourcePath ?? "Unknown"}
                subHeader={
                  op.metadata.metadata.value.fileSize
                    ? formatFileSize(Number(op.metadata.metadata.value.fileSize))
                    : null
                }
                isDownloadable={op.operationStatus === SourceSyncOperationStatus.COMPLETED}
                objectStorageUri={op.objectStorageUri}
                filename={op.metadata.metadata.value.sourcePath ?? "Unknown"}
              />
            ))}
          </GridWrapper>
        ) : (
          <EmptyLayout title="No content" description="No content matches your search" />
        )}
      </FlexWrapper>
    </FlexWrapper>
  );
};

export default EditSourceContentFileUpload;
